import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Container,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Phone as PhoneIcon, 
  Email as EmailIcon,
  Home as HomeIcon,
  Hotel as HotelIcon,
  Search as SearchIcon,
  ContactMail as ContactMailIcon,
  EventAvailable as EventAvailableIcon
} from '@mui/icons-material';
import { getImageUrl } from '../services/api';

const hotelLogoUrl = getImageUrl('JCP.png', 'logos');

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const primaryNavItems = [
    { name: 'Home', path: '/' },
    { name: 'Properties', path: '/properties' },
    { name: 'About Us', path: '/about' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Blog', path: '/blogs' },
    { name: 'Contact', path: '/contact' },
  ];

  const mobileBottomNavItems = [
    { name: 'Home', path: '/', icon: HomeIcon },
    { name: 'Properties', path: '/properties', icon: HotelIcon },
    { name: 'Search', path: '/search', icon: SearchIcon },
    { name: 'Contact', path: '/contact', icon: ContactMailIcon },
    { name: 'Book', path: '/reservation', icon: EventAvailableIcon },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActive = (path) => location.pathname === path;

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Box sx={{ my: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <Box
          component="img"
          src={hotelLogoUrl}
          alt="JCP Estate Agents"
          sx={{ height: 112, width: 'auto', backgroundColor: 'transparent', objectFit: 'contain' }}
        />
      </Box>
      <List>
        {primaryNavItems.map((item) => (
          <ListItem key={item.name} disablePadding>
            <ListItemText 
              primary={
                <Link 
                  to={item.path} 
                  style={{ 
                    textDecoration: 'none', 
                    color: isActive(item.path) ? theme.palette.primary.main : 'inherit',
                    fontWeight: isActive(item.path) ? 600 : 400
                  }}
                >
                  {item.name}
                </Link>
              } 
            />
          </ListItem>
        ))}
        <ListItem disablePadding>
          <Button
            component={Link}
            to="/reservation"
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mx: 2, mt: 2 }}
          >
            Book Now
          </Button>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Top Contact Bar */}
      <Box sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', py: { xs: 0.75, md: 1 } }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: { xs: 1, md: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 3 }, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhoneIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}>01865 554422</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EmailIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
                <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', md: '0.75rem' } }}>northoxford@jcpestateagents.co.uk</Typography>
              </Box>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Typography variant="caption">113 Walton Street, Oxford, OX2 6AJ</Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Navigation */}
      <AppBar position="sticky" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)', boxShadow: '0 8px 16px rgba(102, 126, 234, 0.3)' }}>
        <Container maxWidth="xl">
          <Toolbar sx={{ py: { xs: 0.5, md: 1 }, px: { xs: 0, md: 1 } }}>
            {/* Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 1.5 }}>
              <Box
                component={Link}
                to="/"
                sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
              >
                <Box
                  component="img"
                  src={hotelLogoUrl}
                  alt="JCP Estate Agents"
                  sx={{
                    height: { xs: 60, sm: 80, md: 114 },
                    width: 'auto',
                    backgroundColor: 'transparent',
                    objectFit: 'contain'
                  }}
                />
              </Box>
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
              {primaryNavItems.map((item) => (
                <Button
                  key={item.name}
                  component={Link}
                  to={item.path}
                  sx={{
                    fontWeight: isActive(item.path) ? 700 : 500,
                    color: isActive(item.path) ? '#ffffff' : '#ffffff',
                    backgroundColor: isActive(item.path) ? 'rgba(255, 255, 255, 0.25)' : 'transparent',
                    position: 'relative',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      color: '#ffffff',
                    }
                  }}
                >
                  {item.name}
                </Button>
              ))}
              <Button
                component={Link}
                to="/reservation"
                variant="contained"
                sx={{ 
                  ml: 2,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  fontWeight: 700,
                  px: 3,
                  borderRadius: 2,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 20px rgba(16, 185, 129, 0.3)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Book Now
              </Button>
            </Box>

            {/* Mobile menu button */}
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  color: '#667eea',
                  backgroundColor: mobileOpen ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(102, 126, 234, 0.15)',
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          px: 1,
          py: 0.75,
          backgroundColor: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(14px)',
          borderTop: '1px solid rgba(148, 163, 184, 0.2)',
          boxShadow: '0 -8px 24px rgba(15, 23, 42, 0.08)',
          justifyContent: 'space-between',
        }}
      >
        {mobileBottomNavItems.map((item) => {
          const active = item.path === '/search'
            ? location.pathname === '/search'
            : isActive(item.path);

          return (
            <Button
              key={item.name}
              component={Link}
              to={item.path}
              sx={{
                minWidth: 0,
                flex: 1,
                color: active ? '#2563eb' : '#64748b',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.35,
                py: 0.6,
                borderRadius: 2.5,
                textTransform: 'none',
                fontSize: '0.68rem',
                fontWeight: active ? 700 : 600,
                backgroundColor: active ? 'rgba(37, 99, 235, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(37, 99, 235, 0.08)',
                },
              }}
            >
              <item.icon sx={{ fontSize: 20 }} />
              <Box component="span">{item.name}</Box>
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

export default Navbar;
